<script setup lang="ts">
import { ref, computed } from 'vue'
import { Head, Link, router } from '@inertiajs/vue3'
import { Menu } from 'lucide-vue-next'
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'
import FrequencySelect from '@/components/customer-signup/FrequencySelect.vue'
import MowAreaSelect from '@/components/customer-signup/MowAreaSelect.vue'
import MowDaySelect from '@/components/customer-signup/MowDaySelect.vue'
import QuoteEstimate from '@/components/customer-signup/QuoteEstimate.vue'
import ServiceGate from '@/components/customer-signup/ServiceGate.vue'
import SignupForm from '@/components/customer-signup/SignupForm.vue'
import HomeLayout from '@/layouts/HomeLayout.vue'
import { useSignupStorage } from '@/composables/useSignupStorage'

defineOptions({ layout: HomeLayout })

const { signup, clearSignup } = useSignupStorage()

if (!signup.mow_areas) { signup.mow_areas = 'front_and_back' }

const menuOpen = ref(false)
const isStopped = ref(false)
const currentStep = ref(1)

const subtitle = computed(() => {
  if (currentStep.value === 3) {
    return 'Select your mow area.'
  }
  if (currentStep.value === 4) {
    return 'Select your mow day.'
  }
  if (currentStep.value === 5) {
    return 'Review your custom lawn mowing plan.'
  }
  if (currentStep.value === 6) {
    return 'Please enter your Account & Billing Details.'
  }
  return 'You\'re in luck! Mow Managers services your area.'
})

router.on('success', (event) => {
  if (event.detail.page.url.startsWith('/customer-signup/success')) {
    clearSignup()
  }
})

const navLinks = [
  'Services',
  'Pricing',
  'Questions',
  'Referrals',
  'Testimonials',
  'Contact Us',
]
</script>

<template>
  <div class="bg-white min-h-screen">
    <Head title="Self Verification" />
    <nav class="bg-mm-dark">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex items-center justify-between">
        <Link href="/">
          <img src="/img/logo.svg" alt="Mow Managers" class="h-10 w-auto" />
        </Link>

        <div class="hidden lg:flex items-center gap-6 xl:gap-8">
          <a
            v-for="link in navLinks"
            :key="link"
            href="#"
            class="text-white text-[13px] xl:text-[14px] font-bold uppercase tracking-[1.4px] hover:text-white/80 transition-colors"
          >
            {{ link }}
          </a>
          <Link
            href="/get-started"
            class="border-2 border-white text-white text-[13px] xl:text-[14px] font-bold uppercase tracking-[1.4px] rounded-md px-5 py-2 hover:bg-white hover:text-mm-dark transition-colors cursor-pointer"
          >
            Get Started
          </Link>
        </div>

        <Sheet v-model:open="menuOpen">
          <SheetTrigger as-child>
            <button class="lg:hidden text-white" aria-label="Open menu">
              <Menu class="size-7" />
            </button>
          </SheetTrigger>
          <SheetContent side="right" class="bg-mm-dark border-mm-dark w-[280px] p-0">
            <SheetTitle class="sr-only">Navigation Menu</SheetTitle>
            <div class="flex flex-col px-6 pt-16 gap-1">
              <a
                v-for="link in navLinks"
                :key="link"
                href="#"
                class="text-white text-sm font-bold uppercase tracking-[1.6px] py-3 hover:text-mm-green transition-colors"
                @click="menuOpen = false"
              >
                {{ link }}
              </a>
              <Link
                href="/get-started"
                class="mt-4 bg-mm-green text-white text-sm font-bold uppercase tracking-[1.6px] rounded-md px-6 py-3 text-center hover:bg-mm-green-dark transition-colors cursor-pointer"
                @click="menuOpen = false"
              >
                Get Started
              </Link>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </nav>

    <section class="py-16 lg:py-24">
      <div
        class="mx-auto px-4 sm:px-6"
        :class="[3, 4, 5].includes(currentStep) ? 'max-w-3xl' : 'max-w-xl'"
      >
        <div class="text-center mb-12">
          <h1 class="text-4xl sm:text-5xl lg:text-[56px] font-bold leading-tight lg:leading-[64px]">
            <span class="text-mm-green">Free price quote.</span>
          </h1>
          <p class="text-mm-dark text-[18px] mt-4">
            {{ subtitle }}
          </p>
        </div>

        <ServiceGate v-if="currentStep === 1" @update:stopped="isStopped = $event" />

        <button
          v-if="currentStep === 1 && !isStopped"
          type="button"
          class="w-full h-12 bg-mm-green hover:bg-mm-green-dark text-white font-bold uppercase tracking-[1.6px] text-[14px] rounded-md cursor-pointer transition-colors"
          @click="currentStep = 2"
        >
          Continue
        </button>

        <FrequencySelect v-if="currentStep === 2" />

        <button
          v-if="currentStep === 2"
          type="button"
          class="w-full h-12 bg-mm-green hover:bg-mm-green-dark text-white font-bold uppercase tracking-[1.6px] text-[14px] rounded-md cursor-pointer transition-colors"
          @click="currentStep = 3"
        >
          Continue
        </button>

        <MowAreaSelect
          v-if="currentStep === 3"
          @back="currentStep = 2"
          @next="currentStep = 4"
        />

        <MowDaySelect
          v-if="currentStep === 4"
          @back="currentStep = 3"
          @next="currentStep = 5"
        />

        <QuoteEstimate
          v-if="currentStep === 5"
          @back="currentStep = 4"
          @next="currentStep = 6"
        />

        <SignupForm v-if="currentStep === 6" @back="currentStep = 5" />
      </div>
    </section>
  </div>
</template>
